import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import { getConfigWithToken } from "../config/config";
import { getSuperAdminActivityLogs } from "../config/url";
import classes from "./SuperAdminList.module.css";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;

const formatDate = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleString("en-US", {
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const SuperAdminActivityLogs = () => {
	const navigate = useNavigate();
	const token = useSelector((state) => state?.user?.user?.token) || localStorage.getItem("user_id");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [rows, setRows] = useState([]);
	const [search, setSearch] = useState("");
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	const fetchRows = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getSuperAdminActivityLogs, {
				...requestConfig,
				params: {
					limit: DEFAULT_PAGE_SIZE,
					page,
					...(debouncedSearch ? { search: debouncedSearch } : {}),
				},
			});
			const pagination = response?.data?.pagination || {};
			setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
			setTotalItems(pagination.total || 0);
			setTotalPages(Math.max(1, pagination.totalPages || 1));
		} catch {
			setHasError(true);
			setRows([]);
			setTotalItems(0);
			setTotalPages(1);
		} finally {
			setIsLoading(false);
		}
	}, [debouncedSearch, page, requestConfig]);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	return (
		<div className={classes.page}>
			<div className={classes.headerRow}>
				<h1 className={classes.title}>Activity Logs</h1>
				<p className={classes.subtitle}>Platform activity across organizations.</p>
			</div>

			<div className={classes.toolbar}>
				<label className={classes.searchWrap}>
					<Search size={16} strokeWidth={1.75} />
					<input
						className={classes.searchInput}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search activity logs"
						value={search}
					/>
				</label>
			</div>

			{hasError ? (
				<div className={classes.errorBox}>
					<p>Activity logs could not be loaded.</p>
					<button className={classes.retryButton} onClick={fetchRows} type="button">
						Retry
					</button>
				</div>
			) : (
				<>
					<div className={classes.tableWrap}>
						<table className={classes.table}>
							<thead>
								<tr>
									<th>Action</th>
									<th>User</th>
									<th>Entity</th>
									<th>Client ID</th>
									<th>Created</th>
								</tr>
							</thead>
							<tbody>
								{isLoading ? (
									<tr>
										<td className={classes.empty} colSpan={5}>
											Loading...
										</td>
									</tr>
								) : rows.length === 0 ? (
									<tr>
										<td className={classes.empty} colSpan={5}>
											No activity logs found.
										</td>
									</tr>
								) : (
									rows.map((row) => (
										<tr
											className={classes.row}
											key={row.id}
											onClick={() => navigate(`/activity-logs/${row.id}`)}>
											<td>{row.action}</td>
											<td>{row.user_name || "-"}</td>
											<td>{row.entity_type || "-"}</td>
											<td>{row.clientid || "-"}</td>
											<td>{formatDate(row.created_at)}</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					<div className={classes.pagination}>
						<span>
							Page {page} of {totalPages} ({totalItems} total)
						</span>
						<button
							className={classes.pageButton}
							disabled={page <= 1}
							onClick={() => setPage((current) => current - 1)}
							type="button">
							<ChevronLeft size={16} />
						</button>
						<button
							className={classes.pageButton}
							disabled={page >= totalPages}
							onClick={() => setPage((current) => current + 1)}
							type="button">
							<ChevronRight size={16} />
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default SuperAdminActivityLogs;
